import type { ReactNode } from 'react';
import { cn } from '@discover-lib/utils';
import Checkbox from '@discover-ui/checkbox';
import Label from '@discover-ui/label';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';

export type CheckboxGroupOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type CheckboxGroupProps = {
  options: CheckboxGroupOption[];
  values: string[];
  setValues: (newValues: string[]) => void;
  /** Extra classes on each option row (e.g. tighter vertical rhythm). */
  rowClassName?: string;
  /** Extra classes on each option label (e.g. py-0 for compact lists). */
  labelClassName?: string;
  /** Replace an option's label; `toggle` is the same handler the checkbox uses. */
  renderLabel?: (option: CheckboxGroupOption, toggle: () => void) => ReactNode;
};

export default function CheckboxGroup({
  options,
  values,
  setValues,
  rowClassName,
  labelClassName,
  renderLabel,
}: CheckboxGroupProps) {
  const { track } = useAnalytics();

  const toggle = (option: CheckboxGroupOption) => {
    const checked = !values.includes(option.value);
    track('button_clicked', {
      button_type: 'selection',
      button_copy: option.label,
      button_value: checked,
    });
    setValues(
      checked ? [...values, option.value] : values.filter((value) => value !== option.value)
    );
  };

  return (
    <>
      {options.map((option) => (
        <div key={option.value} className={cn('flex flex-row items-center', rowClassName)}>
          {renderLabel?.(option, () => toggle(option)) ?? (
            <Label
              htmlFor={`${option.value}-checkbox`}
              className={cn(
                'flex-1 cursor-pointer py-1 pr-2 text-sm',
                {
                  'text-gray-400 cursor-not-allowed': option.disabled,
                },
                labelClassName
              )}>
              {option.label}
            </Label>
          )}
          <Checkbox
            id={`${option.value}-checkbox`}
            aria-label={option.label}
            checked={values.includes(option.value)}
            onCheckedChange={() => toggle(option)}
            disabled={option.disabled}
            className="mx-1"
          />
        </div>
      ))}
    </>
  );
}
